/**
 * TipsScreen.js
 * =============
 * Tips & Tricks reminder screen — accessible from the Landing Page.
 * Summarises the key interaction patterns so caregivers and new users
 * can quickly review how gaze-based navigation works.
 *
 * Route: /tips
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TipsScreen.css';

const TIPS = [
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <rect x="2.5" y="1" width="3" height="4.5" rx="0.6" />
        <line x1="4" y1="5.5" x2="4" y2="7" />
        <line x1="2.5" y1="7" x2="5.5" y2="7" />
      </svg>
    ),
    title: 'Use portrait mode on mobile',
    body: 'If using the application on mobile, use it in portrait mode for best camera alignment.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <rect x="1" y="1.5" width="6" height="5" rx="0.6" />
        <line x1="2" y1="4" x2="6" y2="4" strokeDasharray="0.6 0.6" />
      </svg>
    ),
    title: 'Keep device 12 inches away',
    body: 'If on a tablet, keep it at least 12 inches away from the user\'s face so the camera captures the full eye range.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <path d="M2 5.5 C2 3.5 6 3.5 6 1.5" />
        <path d="M2 2.5 C2 4.5 6 4.5 6 6.5" />
      </svg>
    ),
    title: 'Exaggerate directional looks',
    body: 'Mildly exaggerate directional looks, especially up and down — the model responds better to deliberate eye movement.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <circle cx="4" cy="4" r="3" />
        <circle cx="4" cy="4" r="1.2" />
      </svg>
    ),
    title: 'Look directionally to select',
    body: 'Gaze toward a block — up, down, left, or right — and hold it steady. The block will highlight as your gaze accumulates.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <polyline points="2,5 4,2 6,5" />
        <line x1="4" y1="2" x2="4" y2="7" />
      </svg>
    ),
    title: 'Look up to go back',
    body: 'On any response screen, hold your gaze upward to navigate back to the previous level.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 32 32" fill="none">
        <mask id="tips-mask" maskUnits="userSpaceOnUse" x="9.72461" y="14" width="16" height="16" fill="black">
          <rect fill="white" x="9.72461" y="14" width="16" height="16"/>
          <path d="M15.7501 15C16.1642 15 16.5001 15.3358 16.5001 15.75V20.5C16.5001 20.7761 16.7239 21 17.0001 21C17.2762 21 17.5001 20.7761 17.5001 20.5V19.75C17.5001 19.3358 17.8359 19 18.2501 19C18.6642 19 19.0001 19.3358 19.0001 19.75V20.5C19.0001 20.7761 19.2239 21 19.5001 21C19.7762 21 20.0001 20.7761 20.0001 20.5V20.25C20.0001 19.8358 20.3359 19.5 20.7501 19.5C21.1642 19.5 21.5001 19.8358 21.5001 20.25V20.5C21.5001 20.7761 21.7239 21 22.0001 21H22.2501C22.3881 21 22.5001 20.8881 22.5001 20.75C22.5001 20.3358 22.8359 20 23.2501 20C23.6643 20 24.0001 20.3358 24.0001 20.75V24.5C24.0001 26.9853 21.9853 29 19.5001 29C17.9582 29 16.5981 28.2241 15.7872 27.042C14.846 25.9671 13.2031 24.7729 12.1309 23.8926C11.6521 23.4994 11.584 22.768 11.9932 22.3027C12.6974 21.5023 13.8989 22.5875 14.627 23.3662C14.7587 23.507 15.0001 23.4161 15.0001 23.2234V15.75C15.0001 15.3358 15.3359 15 15.7501 15Z"/>
        </mask>
        <path d="M15.7501 15C16.1642 15 16.5001 15.3358 16.5001 15.75V20.5C16.5001 20.7761 16.7239 21 17.0001 21C17.2762 21 17.5001 20.7761 17.5001 20.5V19.75C17.5001 19.3358 17.8359 19 18.2501 19C18.6642 19 19.0001 19.3358 19.0001 19.75V20.5C19.0001 20.7761 19.2239 21 19.5001 21C19.7762 21 20.0001 20.7761 20.0001 20.5V20.25C20.0001 19.8358 20.3359 19.5 20.7501 19.5C21.1642 19.5 21.5001 19.8358 21.5001 20.25V20.5C21.5001 20.7761 21.7239 21 22.0001 21H22.2501C22.3881 21 22.5001 20.8881 22.5001 20.75C22.5001 20.3358 22.8359 20 23.2501 20C23.6643 20 24.0001 20.3358 24.0001 20.75V24.5C24.0001 26.9853 21.9853 29 19.5001 29C17.9582 29 16.5981 28.2241 15.7872 27.042C14.846 25.9671 13.2031 24.7729 12.1309 23.8926C11.6521 23.4994 11.584 22.768 11.9932 22.3027C12.6974 21.5023 13.8989 22.5875 14.627 23.3662C14.7587 23.507 15.0001 23.4161 15.0001 23.2234V15.75C15.0001 15.3358 15.3359 15 15.7501 15Z" fill="#0d5c52"/>
        <path d="M5.35355 9.64645C5.15829 9.45118 4.84171 9.45118 4.64645 9.64645L1.46447 12.8284C1.2692 13.0237 1.2692 13.3403 1.46447 13.5355C1.65973 13.7308 1.97631 13.7308 2.17157 13.5355L5 10.7071L7.82843 13.5355C8.02369 13.7308 8.34027 13.7308 8.53553 13.5355C8.7308 13.3403 8.7308 13.0237 8.53553 12.8284L5.35355 9.64645ZM5 18H5.5L5.5 10H5H4.5L4.5 18H5Z" fill="#0d5c52"/>
      </svg>
    ),
    title: 'Swipe up to go back to topics',
    body: 'In Conversation Mode, swipe up on the screen to return to the listening screen and start a new prompt.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <rect x="1" y="2" width="6" height="4" rx="0.5" />
        <line x1="2.5" y1="2" x2="2.5" y2="1.2" />
        <line x1="5.5" y1="2" x2="5.5" y2="1.2" />
      </svg>
    ),
    title: 'Speed is adjustable',
    body: 'Open Settings to slow down or speed up how quickly gaze registers — slower is better for first-time users.',
  },
  {
    icon: (
      <svg viewBox="0 0 8 8" width="22" height="22" fill="none"
        stroke="#0d5c52" strokeWidth="1">
        <circle cx="4" cy="3.5" r="1.5" />
        <path d="M1.5 7C1.5 5.6 2.6 4.5 4 4.5C5.4 4.5 6.5 5.6 6.5 7" />
      </svg>
    ),
    title: 'Calibrate for accuracy',
    body: 'Use Eye Calibration before each session for the best up-gaze detection. It only takes a few seconds.',
  },
];

export default function TipsScreen() {
  const navigate = useNavigate();

  return (
    <div className="tips-root">
      <div className="tips-container">

        <header className="tips-header">
          <button
            className="tips-back-btn"
            onClick={() => navigate('/landing')}
            aria-label="Back to home"
          >
            ← Back
          </button>
          <h1 className="tips-title">Tips &amp; Tricks</h1>
          <p className="tips-subtitle">Quick reminders for getting the most out of Eye2Voice</p>
        </header>

        <ul className="tips-list">
          {TIPS.map((tip, i) => (
            <li key={i} className="tips-card">
              <span className="tips-icon-tile" aria-hidden="true">
                {tip.icon}
              </span>
              <div className="tips-text">
                <p className="tips-card-title">{tip.title}</p>
                <p className="tips-card-body">{tip.body}</p>
              </div>
            </li>
          ))}
        </ul>

      </div>
    </div>
  );
}
